import { Shield, Users, Heart, CheckCircle, Phone, Scale } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CTASection from "@/components/CTASection";
import QuickContact from "@/components/QuickContact";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const AssaultFamilyViolence = () => {
  return (
    <div className="min-h-screen">
      <Header />

      <section className="relative h-[60vh] flex items-center justify-center bg-gradient-hero text-primary-foreground">
        <div className="container mx-auto px-4 relative z-10 text-center">
          <Shield className="h-20 w-20 mx-auto mb-6 text-accent" />
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Assault & Family Violence Defense</h1>
          <p className="text-xl mb-8 max-w-3xl mx-auto opacity-95">
            Protecting Your Rights and Your Future in Domestic Violence Cases
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" variant="hero">
              <Link to="/booking">Schedule Consultation</Link>
            </Button>
            <a href="tel:8329303059">
              <Button size="lg" variant="outline" className="border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary">
                <Phone className="mr-2 h-5 w-5" />
                Call: (832) 930-3059
              </Button>
            </a>
          </div>
        </div>
      </section>

      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8">Family Violence & Assault Defense in Texas</h2>
            <p className="text-lg text-muted-foreground mb-6">
              Family violence and assault charges carry serious consequences beyond criminal penalties. A conviction can result in protective orders, loss of gun rights, custody issues, and permanent impact on your reputation. Even false accusations require immediate, aggressive defense.
            </p>

            <div className="bg-card p-8 rounded-lg shadow-lg mb-12">
              <h3 className="text-2xl font-bold mb-4">We Defend Against All Assault Charges:</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  "Simple Assault",
                  "Aggravated Assault",
                  "Assault Family Violence",
                  "Domestic Assault",
                  "Assault Bodily Injury",
                  "Assault Causing Serious Bodily Injury",
                  "Assault on Public Servant",
                  "Sexual Assault"
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-accent flex-shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-destructive/10 border border-destructive/20 p-6 rounded-lg mb-12">
              <h3 className="text-xl font-bold mb-3 text-destructive flex items-center gap-2">
                <Heart className="h-6 w-6" />
                Unique Challenges of Family Violence Cases
              </h3>
              <p className="text-muted-foreground mb-4">
                Family violence cases are treated differently under Texas law. Even if the alleged victim wants to drop charges, prosecutors often proceed anyway. Consequences include:
              </p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-destructive">•</span>
                  <span>Automatic protective orders keeping you from your home and family</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-destructive">•</span>
                  <span>Loss of firearm rights</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-destructive">•</span>
                  <span>Impact on child custody and visitation</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-destructive">•</span>
                  <span>Enhanced penalties for future offenses</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-destructive">•</span>
                  <span>Employment consequences</span>
                </li>
              </ul>
            </div>

            <h3 className="text-3xl font-bold mb-6">Defense Strategies</h3>
            <div className="space-y-6 mb-12">
              <div className="bg-card p-6 rounded-lg">
                <h4 className="text-xl font-bold mb-3 flex items-center gap-2">
                  <Shield className="h-6 w-6 text-accent" />
                  Self-Defense
                </h4>
                <p className="text-muted-foreground">
                  You have the right to defend yourself from harm. We demonstrate that your actions were reasonable and necessary to protect yourself or others from imminent danger.
                </p>
              </div>

              <div className="bg-card p-6 rounded-lg">
                <h4 className="text-xl font-bold mb-3 flex items-center gap-2">
                  <Scale className="h-6 w-6 text-accent" />
                  Defense of Others
                </h4>
                <p className="text-muted-foreground">
                  Similar to self-defense, you may use reasonable force to protect family members, especially children, from violence.
                </p>
              </div>

              <div className="bg-card p-6 rounded-lg">
                <h4 className="text-xl font-bold mb-3 flex items-center gap-2">
                  <Users className="h-6 w-6 text-accent" />
                  False Accusations
                </h4>
                <p className="text-muted-foreground">
                  Sadly, false domestic violence allegations are common in custody battles, divorces, or relationship disputes. We investigate the accuser's motives, inconsistencies in their story, and gather evidence proving your innocence.
                </p>
              </div>

              <div className="bg-card p-6 rounded-lg">
                <h4 className="text-xl font-bold mb-3">Lack of Evidence</h4>
                <p className="text-muted-foreground">
                  Prosecution must prove guilt beyond reasonable doubt. We challenge witness credibility, lack of physical evidence, and inconsistent statements.
                </p>
              </div>
            </div>

            <h3 className="text-3xl font-bold mb-6">Texas Assault Penalties</h3>
            <div className="space-y-4 mb-12">
              <div className="bg-card p-6 rounded-lg">
                <h4 className="font-bold text-lg mb-2">Class C Misdemeanor Assault</h4>
                <p className="text-muted-foreground mb-2">Assault by threat or offensive contact</p>
                <p className="text-sm text-muted-foreground">Penalty: Fine up to $500</p>
              </div>
              <div className="bg-card p-6 rounded-lg">
                <h4 className="font-bold text-lg mb-2">Class A Misdemeanor Assault</h4>
                <p className="text-muted-foreground mb-2">Assault causing bodily injury or family violence</p>
                <p className="text-sm text-muted-foreground">Penalty: Up to 1 year in jail, fine up to $4,000</p>
              </div>
              <div className="bg-card p-6 rounded-lg">
                <h4 className="font-bold text-lg mb-2">Third-Degree Felony Aggravated Assault</h4>
                <p className="text-muted-foreground mb-2">Assault with serious bodily injury or with weapon</p>
                <p className="text-sm text-muted-foreground">Penalty: 2-10 years prison, fine up to $10,000</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <QuickContact 
        title="Facing Assault or Family Violence Charges?"
        description="Don't face these serious charges alone. Contact us immediately for aggressive defense."
      />

      <CTASection 
        title="Protect Your Rights and Your Family"
        description="Assault and family violence charges require immediate, experienced legal representation."
      />
      <Footer />
    </div>
  );
};

export default AssaultFamilyViolence;
